/**
 * ⚙️ Rep-only assumptions drawer: overrides, the VPP program value (Octopus
 * keeps it), engine warnings, and every assumption with its effective date
 * and source. Nothing here is printed.
 */
import { OCTOPUS_PLAN, TDUS, TERM_OPTIONS } from "../../config";
import type { BidResult } from "../../engine/bid";
import type { AppState, RepSettings } from "../state";
import { drawerAssumptions, formatAssumption, provenance, repNotes, usd } from "../viewmodel";

const esc = (s: string): string => s.replace(/[&<>"]/g, (c) => ({ "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;" })[c]!);
const cents = (dollars: number): string => String(Number((dollars * 100).toFixed(4)));

function field(id: string, label: string, bind: string, value: string, unit: string, placeholder: string): string {
  return `<div class="dfield">
    <label for="${id}">${label}</label>
    <div class="input-unit sm"><input id="${id}" type="text" inputmode="decimal" autocomplete="off" data-rep="${bind}" value="${esc(value)}" placeholder="${esc(placeholder)}" /><span>${unit}</span></div>
  </div>`;
}

/** Override form. Rendered when the drawer opens or the TDU changes. */
export function drawerFormHtml(state: AppState, rep: RepSettings): string {
  const tdu = TDUS[state.tdu];
  const t = rep.tdu[state.tdu];
  const vol = tdu.volumetricRate;
  const volOptions = [
    `<label class="radio-line"><input type="radio" name="volChoice" value="default" data-rep="tdu.${state.tdu}.volChoice" ${t.volChoice === "default" ? "checked" : ""}/>
      <span><strong>${cents(vol.value)}¢</strong> eff. ${vol.effectiveDate}${vol.note ? ` <em class="warn">${/temporary/i.test(vol.note) ? "temporary" : "note"}</em>` : ""} <small>(default)</small></span></label>`,
    ...(vol.alternates ?? []).map(
      (a, i) => `<label class="radio-line"><input type="radio" name="volChoice" value="alt:${i}" data-rep="tdu.${state.tdu}.volChoice" ${t.volChoice === `alt:${i}` ? "checked" : ""}/>
      <span><strong>${cents(a.value)}¢</strong> eff. ${a.effectiveDate} · ${esc(a.label)} <small>(PUCT)</small></span></label>`,
    ),
    `<label class="radio-line"><input type="radio" name="volChoice" value="custom" data-rep="tdu.${state.tdu}.volChoice" ${t.volChoice === "custom" ? "checked" : ""}/>
      <span>Custom</span></label>`,
  ].join("");

  return `
  <section class="dsec">
    <h3>Octopus plan</h3>
    <div class="dgrid">
      ${TERM_OPTIONS.map((term) => field(`ov-rate-${term}`, `${term}-month energy rate`, `octRateCents.${term}`, rep.octRateCents[term], "¢/kWh", cents(OCTOPUS_PLAN.energyRateByTerm[term].value))).join("")}
      ${field("ov-base", "Base fee", "octBaseFee", rep.octBaseFee, "$/mo", String(OCTOPUS_PLAN.baseFee.value))}
    </div>
  </section>
  <section class="dsec">
    <h3>${tdu.name} delivery (TDU)</h3>
    <fieldset class="vol-choice"><legend>Delivery charge per kWh</legend>${volOptions}
      ${t.volChoice === "custom" ? field("ov-vol", "Custom delivery rate", `tdu.${state.tdu}.volCents`, t.volCents, "¢/kWh", cents(vol.value)) : ""}
    </fieldset>
    ${vol.note ? `<p class="dnote">${esc(vol.note)}</p>` : ""}
    <div class="dgrid">
      ${field("ov-cust", "Customer charge", `tdu.${state.tdu}.customer`, t.customer, "$/mo", String(tdu.customerCharge.value))}
      ${field("ov-meter", "Metering charge", `tdu.${state.tdu}.meter`, t.meter, "$/mo", String(tdu.meteringCharge.value))}
    </div>
  </section>
  <section class="dsec">
    <h3>Home &amp; program</h3>
    <div class="dgrid">
      <div class="dfield"><label for="ov-arch">Home heating</label>
        <select id="ov-arch" data-rep="archetype">
          <option value="electric_heat" ${rep.archetype === "electric_heat" ? "selected" : ""}>Electric heat / heat pump</option>
          <option value="gas_heat" ${rep.archetype === "gas_heat" ? "selected" : ""}>Gas heat</option>
        </select></div>
      ${field("ov-vpp", "VPP credit (Octopus keeps)", "vppRate", rep.vppRate, "$/kWh·mo", String(OCTOPUS_PLAN.vpp.usdPerKwhMonth.value))}
    </div>
    <button type="button" class="btn ghost sm" id="btn-reset-rep">Reset all overrides</button>
  </section>`;
}

/** Live part: program value, warnings, assumption table. Re-rendered on every recompute. */
export function drawerLiveHtml(bid: BidResult | null, missing: string[]): string {
  if (!bid) {
    return `<section class="dsec"><p class="dnote">Enter ${missing.join(", ")} to see the full assumption list.</p></section>`;
  }
  const vpp = bid.repOnly.vppProgramValue;
  const program =
    vpp.batteryKwh > 0
      ? `<div class="program">
          <div class="program-head">VPP program value <small>(rep only · retained by Octopus to fund the free battery)</small></div>
          <div class="program-vals">
            <span><strong>${usd(vpp.monthly)}</strong>/mo</span>
            ${TERM_OPTIONS.map((t) => `<span>${t} mo: <strong>${usd(vpp.byTerm[t])}</strong></span>`).join("")}
          </div>
          <p class="dnote">${vpp.batteryKwh} kWh × ${usd(vpp.usdPerKwhMonth)}/kWh·mo. Not customer savings; never shown to the customer or printed.</p>
        </div>`
      : `<div class="program off">VPP program value: battery is off ($0).</div>`;

  const notes = repNotes(bid);
  const warnings = notes.length
    ? `<ul class="dwarn">${notes.map((w) => `<li>${esc(w)}</li>`).join("")}</ul>`
    : "";

  const rows = drawerAssumptions(bid)
    .map((a) => {
      const p = provenance(a);
      const flags = [
        a.placeholder ? `<span class="chip warn">placeholder</span>` : "",
        a.overridden ? `<span class="chip over">overridden${a.defaultValue !== undefined ? ` (was ${esc(formatAssumption(a, a.defaultValue))})` : ""}</span>` : "",
        a.key === "tdu.volumetricRate" && !a.overridden && /temporary/i.test(a.note ?? "") ? `<span class="chip warn">temporary</span>` : "",
        a.key.startsWith("octopus.vpp.") ? `<span class="chip rep">rep only</span>` : "",
      ].join("");
      return `<tr${a.overridden ? ' class="is-over"' : ""}>
        <th scope="row">${esc(a.label)} ${flags}</th>
        <td class="num">${esc(formatAssumption(a))}</td>
        <td class="date">${p.date}</td>
        <td class="src">${esc(p.source)}</td>
      </tr>`;
    })
    .join("");

  return `
  <section class="dsec">${program}${warnings}</section>
  <section class="dsec">
    <h3>Every value in this bid</h3>
    <div class="table-wrap"><table class="atable">
      <thead><tr><th scope="col">Assumption</th><th scope="col">Value</th><th scope="col">Effective</th><th scope="col">Source</th></tr></thead>
      <tbody>${rows}</tbody>
    </table></div>
  </section>`;
}
